/**
 * スキャンの右パネル。概要 + 走査の集計 + 検出の表 + CSV。取消(queued / sent)とレビュー(done)は上の帯。
 * 終わっていない間は 10 秒ごとに読み直す(`useDlpScan`)。
 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";

import type { DlpFinding, DlpScan } from "../../lib/api";
import { jst } from "../../lib/format";
import { useApi } from "../../lib/useApi";
import { KeyValueList, PanelSection, SidePanel, TimeAgo } from "../../ui";
import { bytesText, deviceCell, durationText, employeeCell, scanStatusTag, triggerTag } from "./bits";
import { skipReasonLabel } from "./constants";
import { errorText, useDlpDownload, useDlpPerms, useDlpScan, useDlpScanFindings, useInvalidateDlp } from "./hooks";

const { Text } = Typography;
const FINDINGS_PAGE = 20;

const FINDING_COLUMNS: ColumnsType<DlpFinding> = [
  {
    title: "パス",
    width: 320,
    onCell: (row) => ({ title: String(row.path ?? "") }),
    dataIndex: "path",
    ellipsis: true,
    render: (v: string) => (
      <Text copyable={{ text: v }} title={v} style={{ fontSize: 12 }}>
        {v}
      </Text>
    ),
  },
  { title: "パターン", dataIndex: "pattern", width: 160, },
  { title: "一致", dataIndex: "match_count", width: 90, align: "right", render: (v: number) => <span className="ui-num">{v}</span> },
  { title: "サイズ", dataIndex: "size_bytes", width: 110, align: "right", render: (v: number) => <span className="ui-num">{bytesText(v)}</span> },
];

/** レビュー(done → reviewed)。所見を 1 つ付ける。approver 以上。 */
function ReviewButton({ scan }: { scan: DlpScan }) {
  const api = useApi();
  const invalidate = useInvalidateDlp();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<{ note: string }>();
  const review = useMutation({
    mutationFn: (note: string) => api.dlpReviewScan(scan.id, { note }),
    onSuccess: () => {
      void message.success("確認済みにした(監査ログに記録した)");
      setOpen(false);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });
  return (
    <>
      <Button type="primary" size="small" onClick={() => setOpen(true)}>
        確認済みにする
      </Button>
      <Modal
        open={open}
        title={`スキャン #${scan.id} を確認済みにする`}
        okText="確認済みにする"
        cancelText="やめる"
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        okButtonProps={{ loading: review.isPending }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={(v) => review.mutate(v.note?.trim() ?? "")}>
          <Form.Item name="note" label="所見" extra={`検出 ${scan.findings_count} 件をどう扱ったか(本人に確認した・問題なし など)`}>
            <Input.TextArea rows={3} maxLength={1000} placeholder="所見(任意)" autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

/** 取消(queued / sent のみ。running はサーバーが 409)。 */
function CancelButton({ scan }: { scan: DlpScan }) {
  const api = useApi();
  const invalidate = useInvalidateDlp();
  const cancel = useMutation({
    mutationFn: () => api.dlpCancelScan(scan.id),
    onSuccess: () => {
      void message.success("取り消した");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });
  return (
    <Popconfirm
      title="このスキャンを取り消す"
      description="エージェントにはもう渡らない。送信済みなら結果が来ても捨てる。"
      okText="取り消す"
      cancelText="やめる"
      okButtonProps={{ danger: true }}
      onConfirm={() => cancel.mutate()}
    >
      <Button size="small" danger loading={cancel.isPending}>
        取消
      </Button>
    </Popconfirm>
  );
}

export function ScanPanel({ id, onClose }: { id: number; onClose: () => void }) {
  const { canOperate, canApprove } = useDlpPerms();
  const query = useDlpScan(Number.isFinite(id) ? id : null);
  const s = query.data;
  const [page, setPage] = useState(1);
  const findings = useDlpScanFindings(s && s.findings_count > 0 ? id : null, page, FINDINGS_PAGE);
  const download = useDlpDownload();

  if (query.isError) {
    return (
      <SidePanel title={`スキャン #${id}`} onClose={onClose}>
        <Alert type="error" showIcon message="スキャンを取得できなかった" description={errorText(query.error)} />
      </SidePanel>
    );
  }

  const sum = s?.summary ?? null;
  const skipped = Object.entries(sum?.skipped_reasons ?? {}).filter(([, n]) => n > 0);
  const snapshot = s?.policy_snapshot ?? null;

  return (
    <SidePanel
      title={`スキャン #${id}`}
      ariaLabel={`スキャン #${id} の詳細`}
      loading={query.isLoading || !s}
      status={s && scanStatusTag(s)}
      sub={s && <Space size={6} wrap>{deviceCell(s.device)}{triggerTag(s.trigger)}</Space>}
      barActions={
        s && (
          <Space size={6}>
            {canOperate && (s.status === "queued" || s.status === "sent") && <CancelButton scan={s} />}
            {canApprove && s.status === "done" && <ReviewButton scan={s} />}
          </Space>
        )
      }
      onClose={onClose}
    >
      {s && (
        <>
          {s.status === "error" && (
            <Alert type="error" showIcon style={{ marginBottom: 12 }} message="スキャンが失敗した" description={s.error || "理由は返っていない"} />
          )}
          {s.status === "reviewed" && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${s.reviewed_by} が確認済み(${jst(s.reviewed_at)})`}
              description={s.review_note || undefined}
            />
          )}
          <PanelSection title="概要">
            <KeyValueList
              narrow
              items={[
                { label: "端末", value: deviceCell(s.device), always: true },
                { label: "従業員", value: employeeCell(s.employee), always: true },
                { label: "契機", value: triggerTag(s.trigger) },
                { label: "ポリシー", value: snapshot?.name ?? (s.policy_id ? `#${s.policy_id}` : "") },
                { label: "依頼者", value: s.requested_by },
                { label: "依頼", value: <TimeAgo iso={s.created_at} /> },
                { label: "送信", value: s.sent_at ? <TimeAgo iso={s.sent_at} /> : "" },
                { label: "開始", value: s.started_at ? <TimeAgo iso={s.started_at} /> : "" },
                { label: "完了", value: s.finished_at ? jst(s.finished_at) : "" },
                { label: "備考", value: s.note, wide: true },
              ]}
            />
          </PanelSection>
          {snapshot && (
            <PanelSection title="渡した検査内容">
              <KeyValueList
                narrow
                items={[
                  { label: "パターン", value: (snapshot.patterns ?? []).map((p) => p.name).join(" / ") },
                  { label: "場所", value: (snapshot.roots ?? []).join(" / ") },
                  { label: "拡張子", value: (snapshot.extensions ?? []).join(" ") },
                  {
                    label: "上限",
                    value: `${snapshot.max_file_mb ?? "—"} MB / ${snapshot.max_files ?? "—"} ファイル / ${snapshot.time_budget_min ?? "—"} 分`,
                  },
                ]}
              />
            </PanelSection>
          )}
          {sum && (
            <PanelSection title="走査の集計">
              <KeyValueList
                narrow
                unsetWhen={(v) => v == null || v === ""}
                items={[
                  { label: "見つけたファイル", value: sum.files_seen },
                  { label: "読んだファイル", value: sum.files_scanned },
                  { label: "読まなかった", value: sum.files_skipped },
                  { label: "読んだ量", value: bytesText(sum.bytes) },
                  { label: "一致", value: sum.matches },
                  { label: "所要", value: durationText(sum.duration_ms) },
                  {
                    label: "打ち切り",
                    value: sum.truncated ? <Tag color="orange">上限に達して途中で止めた</Tag> : "",
                  },
                  {
                    label: "読まなかった理由",
                    value: skipped.length ? (
                      <Space size={4} wrap>
                        {skipped.map(([k, n]) => (
                          <Tag key={k}>
                            {skipReasonLabel(k)}: {n}
                          </Tag>
                        ))}
                      </Space>
                    ) : (
                      ""
                    ),
                    wide: true,
                  },
                ]}
              />
            </PanelSection>
          )}
          <PanelSection title={`検出(${s.findings_count} 件)`}>
            {s.findings_count === 0 ? (
              <div style={{ color: "var(--ux-text-disabled)" }}>
                {s.status === "done" || s.status === "reviewed" ? "検出なし" : "結果はまだ返っていない"}
              </div>
            ) : (
              <>
                {canOperate && (
                  <div style={{ marginBottom: 8 }}>
                    <Button size="small" onClick={() => void download(`/api/dlp/scans/${s.id}/findings.csv`)}>
                      CSV
                    </Button>
                  </div>
                )}
                {findings.isError && <Alert type="error" showIcon message={errorText(findings.error)} />}
                <Table<DlpFinding>
                  scroll={{ x: "max-content" }}
                  size="small"
                  rowKey="id"
                  columns={FINDING_COLUMNS}
                  dataSource={findings.data?.items ?? []}
                  loading={findings.isFetching}
                  pagination={{
                    current: page,
                    pageSize: FINDINGS_PAGE,
                    total: findings.data?.total ?? s.findings_count,
                    size: "small",
                    showSizeChanger: false,
                    onChange: setPage,
                  }}
                />
              </>
            )}
          </PanelSection>
        </>
      )}
    </SidePanel>
  );
}
